import {effect, inject, Injectable, signal, untracked} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {AuthService} from "@common/services/auth/auth.service"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DismissStartingScreenGQL} from "@platform/components/dialogs/onboarding-dialog/onboarding-dialog.generated"
import {shouldShowStartingScreen} from "@platform/components/dialogs/onboarding-dialog/starting-screen.helpers"

/**
 * Controls the self-service starting/tutorials screen. It shows once per login (not on every app
 * reload) for self-service users who have not permanently dismissed it — and once on first signup,
 * right after onboarding completes. "Don't show this again" persists the dismissal on the backend.
 */
@Injectable({
    providedIn: "root",
})
export class StartingScreenService {
    readonly $visible = signal(false)

    // A login this session "arms" the screen; it then appears as soon as the user is eligible. Cleared
    // once shown. An app reload that only restores a stored token never arms it, so it stays hidden.
    private armed = false

    // True once we have seen the current self-service user still mid-onboarding this session. When
    // their onboardingCompletedAt then flips to set, that is a first-signup completion and the screen
    // shows even without a login event — the login is lost across the onboarding flow's Stripe
    // full-page redirect. An already-onboarded user (seen onboarded from the first emit) never sets
    // this, so a plain reload stays silent.
    private onboardingPendingThisSession = false

    private readonly auth = inject(AuthService)
    private readonly dismissStartingScreenGql = inject(DismissStartingScreenGQL)

    constructor() {
        this.auth.loginCompleted$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.armed = true
            this.tryShow()
        })
        // Switching into the staff self-service preview arms the screen too, so it surfaces on entering
        // the preview just like a real self-service login (this fires only on an explicit switch).
        this.auth.silo.selfServiceActivated$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.armed = true
            this.tryShow()
        })
        this.auth.user$.pipe(takeUntilDestroyed()).subscribe((user) => {
            if (user?.selfServiceSignup === true && !user.onboardingCompletedAt) {
                this.onboardingPendingThisSession = true
            }
            this.tryShow()
        })
        // Re-check once the silo resolves as self-service: completing onboarding refreshes the user
        // before it reloads the silo, so the user$ check above can run while the silo isn't known yet.
        effect(() => {
            if (this.auth.$isSelfServiceSilo()) {
                untracked(() => this.tryShow())
            }
        })
    }

    /** Close the dialog for this session without persisting anything. */
    close(): void {
        this.$visible.set(false)
    }

    /** Persist a permanent dismissal ("Don't show this again"), then close. */
    async dismissForever(): Promise<void> {
        await mutateThrowingErrors(this.dismissStartingScreenGql)({})
        await this.auth.refreshUser()
        this.close()
    }

    private tryShow(): void {
        const user = this.auth.$user()
        // Never surface over the first-run onboarding flow. Onboarding state is a user property the silo
        // doesn't carry, so it stays here rather than in shouldShowStartingScreen. A staff preview is
        // never mid-onboarding (selfServiceSignup is null), so the preview is unaffected.
        const midOnboarding = user?.selfServiceSignup === true && !user.onboardingCompletedAt
        if ((this.armed || this.onboardingPendingThisSession) && !midOnboarding && shouldShowStartingScreen(this.auth.$isSelfServiceSilo(), user)) {
            this.armed = false
            this.onboardingPendingThisSession = false
            this.$visible.set(true)
        }
    }
}
